import { useCallback, useEffect, useState } from 'react';
import { api } from '../../lib/api';

interface TrafficReport {
  id: string;
  area: string;
  severity: 'info' | 'watch' | 'warning' | 'critical';
  title: string;
  summary: string;
  source: string;
  link?: string;
  updated_at: string;
}

interface ReportsResponse {
  reports: TrafficReport[];
  areas: string[];
  links: { label: string; url: string }[];
  updated_at: string;
}

const SEV: Record<
  TrafficReport['severity'],
  { label: string; cls: string; bar: string }
> = {
  critical: {
    label: 'Critical',
    cls: 'bg-red-500/20 text-red-200 border-red-500/40',
    bar: 'bg-red-500',
  },
  warning: {
    label: 'Warning',
    cls: 'bg-orange-500/20 text-orange-100 border-orange-500/40',
    bar: 'bg-orange-500',
  },
  watch: {
    label: 'Watch',
    cls: 'bg-amber-500/15 text-amber-100 border-amber-500/30',
    bar: 'bg-amber-400',
  },
  info: {
    label: 'Info',
    cls: 'bg-slate-700/50 text-slate-200 border-slate-600',
    bar: 'bg-slate-500',
  },
};

/**
 * Traffic *reports* for Bluffton / Fort Wayne — text list, not a map.
 */
export function TrafficWidget() {
  const [data, setData] = useState<ReportsResponse | null>(null);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<string>('all');

  const load = useCallback(() => {
    api<ReportsResponse>('/api/traffic/reports')
      .then((res) => {
        setData(res);
        setError('');
      })
      .catch((e) =>
        setError(e instanceof Error ? e.message : 'Reports unavailable')
      );
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 5 * 60_000);
    return () => clearInterval(t);
  }, [load]);

  if (error) {
    return (
      <div>
        <h3 className="mb-2 text-sm font-bold uppercase tracking-wide text-slate-400">
          Traffic reports
        </h3>
        <p className="text-sm text-slate-500">{error}</p>
        <a
          href="https://511in.org/"
          target="_blank"
          rel="noreferrer"
          className="mt-2 inline-block text-xs text-violet-400 hover:underline"
        >
          Open INDOT 511 →
        </a>
      </div>
    );
  }

  if (!data) {
    return (
      <div>
        <h3 className="mb-2 text-sm font-bold uppercase tracking-wide text-slate-400">
          Traffic reports
        </h3>
        <p className="text-sm text-slate-500">Loading reports…</p>
      </div>
    );
  }

  const areas = ['all', ...data.areas];
  const reports =
    filter === 'all'
      ? data.reports
      : data.reports.filter(
          (r) =>
            r.area.includes(filter.replace(', IN', '')) ||
            filter.includes(r.area.replace(', IN', ''))
        );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="mb-1.5 flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-bold uppercase tracking-wide text-slate-400">
          Traffic reports
        </h3>
        <button
          type="button"
          onClick={load}
          className="ml-auto rounded-lg bg-slate-800 px-2 py-0.5 text-[11px] text-slate-300 hover:bg-slate-700"
        >
          Refresh
        </button>
      </div>

      <div className="mb-2 flex flex-wrap gap-1">
        {areas.map((a) => {
          const key = a === 'all' ? 'all' : a;
          const label = a === 'all' ? 'All' : a.replace(', IN', '');
          return (
            <button
              key={key}
              type="button"
              onClick={() => setFilter(key)}
              className={`rounded-lg px-2 py-0.5 text-[11px] font-semibold ${
                filter === key
                  ? 'bg-violet-600 text-white'
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>

      <ul className="min-h-0 flex-1 space-y-2 overflow-auto pr-0.5">
        {reports.length === 0 && (
          <li className="text-sm text-slate-500">No reports for this area.</li>
        )}
        {reports.map((r) => {
          const s = SEV[r.severity] || SEV.info;
          return (
            <li
              key={r.id}
              className={`rounded-xl border px-2.5 py-2 ${s.cls}`}
            >
              <div className="flex items-start gap-2">
                <span
                  className={`mt-1 h-2 w-2 shrink-0 rounded-full ${s.bar}`}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <span className="text-sm font-bold leading-snug">
                      {r.title}
                    </span>
                    <span className="text-[10px] font-semibold uppercase opacity-80">
                      {s.label}
                    </span>
                  </div>
                  <p className="mt-0.5 text-xs leading-snug opacity-90">
                    {r.summary}
                  </p>
                  <div className="mt-1 flex flex-wrap gap-2 text-[10px] opacity-70">
                    <span>{r.area}</span>
                    <span>· {r.source}</span>
                    {r.link && (
                      <a
                        href={r.link}
                        target="_blank"
                        rel="noreferrer"
                        className="text-violet-300 hover:underline"
                      >
                        Details →
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {data.links?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 border-t border-slate-700/60 pt-2 text-[10px]">
          {data.links.map((l) => (
            <a
              key={l.url}
              href={l.url}
              target="_blank"
              rel="noreferrer"
              className="text-violet-400 hover:underline"
            >
              {l.label} ↗
            </a>
          ))}
        </div>
      )}
      <p className="mt-1 text-[10px] text-slate-600">
        Updated {new Date(data.updated_at).toLocaleTimeString()} · text reports
        (not a map)
      </p>
    </div>
  );
}
